import { IsIn, IsOptional, IsString } from 'class-validator';

/**
 * Response DTO for Motive connection status (PD-14140).
 *
 * Motive (gomotive.com) is a non-OAuth integration: the admin supplies the
 * org's static API key, stored (encrypted) in the instance _secret_store.
 * The key is validated against the Motive API at connect time and never
 * returned to the client afterwards.
 */
export class MotiveConnectionStatusDto {
	public connected!: boolean;
	public connectedDate?: string;
	public connectedByUserName?: string;
	/** Number of vehicles in the tenant's Motive fleet (from the last import). */
	public vehicleCount?: number;
	/** Number of Workd vehicles currently paired to a Motive vehicle. */
	public linkedVehicleCount?: number;
}

/**
 * Request DTO for connecting (or reconnecting) Motive.
 *
 * On reconnect the `apiKey` may be omitted/blank to keep the already-stored
 * key (we never return the encrypted secret to pre-fill the form). On an
 * initial connect the key is required and is validated with a live call to
 * the Motive API before being stored (enforced in the service).
 */
export class MotiveConnectRequestDto {
	@IsOptional()
	@IsString()
	public apiKey?: string;
}

/**
 * One vehicle from the tenant's Motive fleet, shaped for the pairing
 * dropdown in the vehicle editor. `linkedVehicleId/Name` identify the Workd
 * vehicle already paired to it (so the UI can disable / annotate options).
 */
export class MotiveVehicleOptionDto {
	/** Motive's vehicle id (stringified — bigint-safe). */
	public motiveVehicleId!: string;
	/** Motive's fleet unit number (e.g. "1000"). */
	public number!: string | null;
	public make!: string | null;
	public model!: string | null;
	public year!: string | null;
	public vin!: string | null;
	/** Motive vehicle status: active / deactivated. */
	public status!: string | null;
	public linkedVehicleId!: number | null;
	public linkedVehicleName!: string | null;
}

export class MotiveVehiclesResponseDto {
	public vehicles!: Array<MotiveVehicleOptionDto>;
}

/**
 * Dashcam positions our UI offers. Motive's Dashcam supports
 * front_facing/driver_facing; Omnicam adds side/rear positions — we pass
 * the value through, so extending this list later is a one-line change.
 */
export const MOTIVE_DASHCAM_POSITIONS = [
	'front_facing',
	'driver_facing',
] as const;

export type MotiveDashcamPosition = (typeof MOTIVE_DASHCAM_POSITIONS)[number];

/**
 * Request DTO for capturing a dashcam image from a vehicle's camera
 * (PD-14381). The capture is asynchronous on Motive's side — the response
 * carries a request id the client polls.
 */
export class MotiveDashcamRequestDto {
	@IsOptional()
	@IsString()
	@IsIn(MOTIVE_DASHCAM_POSITIONS)
	public position?: MotiveDashcamPosition;
}

/** A dashcam capture request accepted by Motive (poll for the result). */
export class MotiveDashcamCaptureDto {
	public requestId!: number;
	/** Motive camera type routing the poll URL: dashcam / omnicam. */
	public cameraType!: string;
	public status!: string;
}

/** Poll result for a dashcam capture. */
export class MotiveDashcamStatusDto {
	/** pending | success | failed */
	public status!: string;
	/**
	 * Pre-signed (EXPIRING) image URL when status=success — display
	 * immediately; anything that must persist re-downloads server-side.
	 */
	public imageUrl?: string;
	public expiresAt?: string;
	/** Motive's reason when status=failed (e.g. "Request timed out"). */
	public failureReason?: string;
}

/**
 * Result summary of a Motive vehicle import run (returned by the manual
 * "Sync vehicles" action and logged by the nightly job).
 */
export class MotiveImportResultDto {
	/** Workd vehicles created from Motive vehicles. */
	public created!: number;
	/** Already-linked Workd vehicles refreshed with Motive data. */
	public updated!: number;
	/** Make/model catalog values auto-created during the import. */
	public catalogValuesCreated!: number;
	/** Motive vehicles seen in the fleet (for the status card). */
	public total!: number;
}
